import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';

const PlateCalculatorModal = ({ isOpen, onClose, initialWeight, initialBarWeight, exerciseId }) => {
  const [targetWeightStr, setTargetWeightStr] = useState(initialWeight != null ? String(initialWeight) : '60');
  const [barWeightStr, setBarWeightStr] = useState(initialBarWeight != null ? String(initialBarWeight) : '20');

  useEffect(() => {
    if (isOpen) {
      setTargetWeightStr(initialWeight != null ? String(initialWeight) : '60');
      setBarWeightStr(initialBarWeight != null ? String(initialBarWeight) : '20');
    }
  }, [initialWeight, initialBarWeight, isOpen]);

  if (!isOpen) return null;

  const targetWeight = parseFloat(targetWeightStr) || 0;
  const barWeight = parseFloat(barWeightStr) || 0;

  const adjustTargetWeight = (delta) => {
    const current = parseFloat(targetWeightStr) || 0;
    const next = Math.max(0, Math.round((current + delta) * 100) / 100);
    setTargetWeightStr(String(next));
  };

  const calculatePlates = () => {
    let remaining = (targetWeight - barWeight) / 2;
    if (remaining <= 0) return [];

    const availablePlates = [20, 10, 5, 2.5, 1.25];
    const platesUsed = [];

    for (let plate of availablePlates) {
      while (remaining >= plate) {
        platesUsed.push(plate);
        remaining -= plate;
      }
    }
    return platesUsed;
  };

  const plates = calculatePlates();

  const getPlateColor = (weight) => {
    switch(weight) {
      case 20: return '#ff4757'; // Vibrant Red
      case 10: return '#2ed573'; // Vibrant Green
      case 5: return '#3867d6';  // Dark Blue
      case 2.5: return '#a29bfe'; // Lighter Purple
      case 1.25: return '#ffa502'; // Vibrant Orange
      default: return '#bdc3c7';
    }
  };

  const getPlateHeight = (weight) => {
    switch(weight) {
      case 20: return '120px';
      case 10: return '80px';
      case 5: return '60px';
      case 2.5: return '40px';
      case 1.25: return '30px';
      default: return '50px';
    }
  };

  const getPlateWidth = (weight) => {
    switch(weight) {
      case 20: return '24px';
      case 10: return '18px';
      case 5: return '14px';
      case 2.5: return '12px';
      case 1.25: return '10px';
      default: return '15px';
    }
  };

  return (
    <div 
      className="modal-overlay" 
      style={{ 
        position: 'fixed', 
        top: 0, 
        left: 0, 
        right: 0, 
        bottom: 0, 
        backgroundColor: 'rgba(0,0,0,0.8)', 
        zIndex: 9999, 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center', 
        padding: '1rem',
        overflowY: 'auto',
        WebkitOverflowScrolling: 'touch',
        touchAction: 'manipulation'
      }} 
      onClick={onClose}
    >
      <div 
        className="card modal-card" 
        style={{ 
          width: '100%', 
          maxWidth: '420px', 
          backgroundColor: 'var(--bg-card)', 
          border: '2px solid var(--secondary-color)', 
          borderRadius: '15px', 
          position: 'relative',
          transform: 'none'
        }} 
        onClick={e => e.stopPropagation()}
      >
        <button className="btn-icon" onClick={onClose} style={{ position: 'absolute', top: '10px', right: '10px' }} title="Fermer">
          <X size={24} color="var(--text-secondary)" />
        </button>
        
        <h3 style={{ marginTop: 0, color: 'var(--primary-color)', textAlign: 'center', marginBottom: '1.2rem' }}>Calculateur de Disques</h3>
        
        <div style={{ display: 'flex', gap: '1rem', marginBottom: '1rem' }}>
          <div style={{ flex: 1 }}>
            <label className="form-label" style={{ fontSize: '0.85rem' }}>Poids Cible (kg)</label>
            <input 
              type="number" 
              step="any"
              inputMode="decimal"
              className="form-control" 
              value={targetWeightStr} 
              onChange={(e) => setTargetWeightStr(e.target.value)} 
              style={{ textAlign: 'center', fontSize: '1.25rem', fontWeight: 'bold' }} 
            />
            <div style={{ display: 'flex', justifyContent: 'center', gap: '0.3rem', marginTop: '0.4rem' }}>
              <button type="button" className="btn btn-outline" style={{ padding: '0.2rem 0.4rem', fontSize: '0.75rem' }} onClick={() => adjustTargetWeight(-5)}>-5</button>
              <button type="button" className="btn btn-outline" style={{ padding: '0.2rem 0.4rem', fontSize: '0.75rem' }} onClick={() => adjustTargetWeight(-2.5)}>-2.5</button>
              <button type="button" className="btn btn-outline" style={{ padding: '0.2rem 0.4rem', fontSize: '0.75rem' }} onClick={() => adjustTargetWeight(2.5)}>+2.5</button>
              <button type="button" className="btn btn-outline" style={{ padding: '0.2rem 0.4rem', fontSize: '0.75rem' }} onClick={() => adjustTargetWeight(5)}>+5</button>
            </div>
          </div>
          <div style={{ flex: 1 }}>
            <label className="form-label" style={{ fontSize: '0.85rem' }}>Poids à vide (kg)</label>
            <input 
              type="number" 
              step="any"
              inputMode="decimal"
              className="form-control" 
              value={barWeightStr} 
              onChange={(e) => setBarWeightStr(e.target.value)} 
              style={{ textAlign: 'center', fontSize: '1.25rem', fontWeight: 'bold' }} 
            />
            <div style={{ display: 'flex', justifyContent: 'center', gap: '0.3rem', marginTop: '0.4rem' }}>
              <button type="button" className="btn btn-outline" style={{ padding: '0.2rem 0.4rem', fontSize: '0.75rem', fontWeight: barWeight === 10 ? 'bold' : 'normal', borderColor: barWeight === 10 ? 'var(--primary-color)' : 'var(--border-color)' }} onClick={() => setBarWeightStr('10')}>10</button>
              <button type="button" className="btn btn-outline" style={{ padding: '0.2rem 0.4rem', fontSize: '0.75rem', fontWeight: barWeight === 15 ? 'bold' : 'normal', borderColor: barWeight === 15 ? 'var(--primary-color)' : 'var(--border-color)' }} onClick={() => setBarWeightStr('15')}>15</button>
              <button type="button" className="btn btn-outline" style={{ padding: '0.2rem 0.4rem', fontSize: '0.75rem', fontWeight: barWeight === 20 ? 'bold' : 'normal', borderColor: barWeight === 20 ? 'var(--primary-color)' : 'var(--border-color)' }} onClick={() => setBarWeightStr('20')}>20</button>
            </div>
          </div>
        </div>

        {targetWeight <= barWeight ? (
          <div style={{ textAlign: 'center', color: 'var(--text-secondary)', padding: '2rem 0' }}>
            La barre seule suffit ! 💪
          </div>
        ) : (
          <>
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center', 
              margin: '3rem 0',
              position: 'relative',
              height: '140px'
            }}>
              {/* Bar Sleeve */}
              <div style={{ width: '140px', height: '16px', backgroundColor: '#7f8c8d', borderRadius: '2px', position: 'absolute', right: '50%' }}></div>
              <div style={{ width: '140px', height: '16px', backgroundColor: '#7f8c8d', borderRadius: '2px', position: 'absolute', left: '50%' }}></div>
              {/* Bar center stopper */}
              <div style={{ width: '20px', height: '30px', backgroundColor: '#34495e', zIndex: 2, borderRadius: '4px' }}></div>
              
              {/* Plates Right */}
              <div style={{ display: 'flex', position: 'absolute', left: 'calc(50% + 10px)', alignItems: 'center', gap: '2px' }}>
                {plates.map((p, i) => (
                  <div key={`r-${i}`} style={{
                    width: getPlateWidth(p),
                    height: getPlateHeight(p),
                    backgroundColor: getPlateColor(p),
                    borderRadius: '4px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#fff',
                    fontSize: '0.6rem',
                    fontWeight: 'bold',
                    boxShadow: 'inset -2px 0 5px rgba(0,0,0,0.2)',
                    writingMode: 'vertical-rl',
                    textOrientation: 'mixed'
                  }}>
                    {p}
                  </div>
                ))}
              </div>
              
              {/* Plates Left */}
              <div style={{ display: 'flex', position: 'absolute', right: 'calc(50% + 10px)', alignItems: 'center', gap: '2px', flexDirection: 'row-reverse' }}>
                {plates.map((p, i) => (
                  <div key={`l-${i}`} style={{
                    width: getPlateWidth(p),
                    height: getPlateHeight(p),
                    backgroundColor: getPlateColor(p),
                    borderRadius: '4px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#fff',
                    fontSize: '0.6rem',
                    fontWeight: 'bold',
                    boxShadow: 'inset 2px 0 5px rgba(0,0,0,0.2)',
                    writingMode: 'vertical-rl',
                    textOrientation: 'mixed'
                  }}>
                    {p}
                  </div>
                ))}
              </div>
            </div>

            <div style={{ textAlign: 'center', color: 'var(--text-secondary)' }}>
              De chaque côté : 
              <div style={{ fontWeight: 'bold', color: 'var(--primary-color)', marginTop: '0.5rem' }}>
                {plates.join(' + ')} kg
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default PlateCalculatorModal;
